import { ToggleGroup, tokens } from '@hotjar/hotsauce';
import type { TippyProps } from '@tippyjs/react';
import Tippy from '@tippyjs/react';
import styled from '@emotion/styled';
import { useState } from 'react';
import { DEFAULT_VALUE, RELATIVE_DATE_RANGES } from './constant';
import { DATE_RANGE_TYPE } from './DateRangeFilterType';
import type { DateRange, MinimalDateConstraint } from './DateRangeFilterType';
import type { AbstractSingleFilterDropdownProps } from '../SingleFilterType';
import { hideOnEsc } from '../../../../utils/hideOnEsc';
import {
  getMinDateMessage,
  isDateRangeBeforeMinDate,
  makeAllDateRange,
} from './DateRangeFilterUtils';
import { getDateRangeLabel } from './DateRangeFilterFormatter';
import { useTippy } from '../../useTippy';

const StyledTippy = styled(Tippy)`
  color: ${tokens.color.text_default};
  ${tokens.elevation(2)};
  background-color: ${tokens.color.ui_background};

  .tippy-content {
    padding: 0;
  }
`;

const StyledToggleGroup = styled(ToggleGroup)<{
  isAutoSelected: boolean;
}>`
  // We hack the color to look like a tokens.color.bg_warning
  filter: ${({ isAutoSelected }) => (isAutoSelected ? 'sepia(15%);' : 'none')};
`;

const LazyTippy = (props: TippyProps) => {
  const [mounted, setMounted] = useState(false);

  const computedProps = { ...props };
  const { content, render } = props;

  if (render) {
    computedProps.render = (...args) => (mounted ? render(...args) : '');
  } else {
    computedProps.content = mounted ? content : '';
  }

  return (
    <StyledTippy
      onMount={() => setMounted(true)}
      onHidden={() => setMounted(false)}
      {...computedProps}
    />
  );
};

function stringify(currentValue: DateRange): string {
  return currentValue.type === DATE_RANGE_TYPE.RELATIVE
    ? `${currentValue.amount}${currentValue.unit}`
    : currentValue.type;
}

const Z_INDEX = 13; // stay under .sidebar-feature and above .paging-row

interface DateRangeToggleGroupProps extends AbstractSingleFilterDropdownProps<DateRange> {
  hideAllDatesOption?: boolean;
  dateConstraint?: MinimalDateConstraint;
  isAutoSelected?: boolean;
  renderDropdown: (options: { close: () => void }) => JSX.Element;
}

export function DateRangeToggleGroup({
  currentValue,
  optionalDefaultValue = DEFAULT_VALUE,
  renderDropdown,
  hideAllDatesOption,
  dateConstraint,
  isAutoSelected,
  onApply,
  filterName,
}: DateRangeToggleGroupProps): JSX.Element {
  const tippy = useTippy();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const currentStringValue = isDropdownVisible
    ? DATE_RANGE_TYPE.ABSOLUTE
    : stringify(currentValue || optionalDefaultValue);

  const toggleOptionList = [
    // Custom toggle
    <ToggleGroup.Toggle icon="date" value={DATE_RANGE_TYPE.ABSOLUTE} key={DATE_RANGE_TYPE.ABSOLUTE}>
      {currentValue?.type === DATE_RANGE_TYPE.ABSOLUTE
        ? `${getDateRangeLabel(currentValue)}`
        : 'Custom'}
    </ToggleGroup.Toggle>,

    // Relative toggles
    ...RELATIVE_DATE_RANGES.map((item) => {
      const optionValue = stringify(item);
      const optionLabel =
        currentStringValue === optionValue
          ? `${item.amount} ${item.unit}`
          : `${item.amount}${item.unit.charAt(0)}`;
      return (
        <ToggleGroup.Toggle
          value={optionValue}
          isDisabled={isDateRangeBeforeMinDate(item, dateConstraint)}
          disabledTooltipText={getMinDateMessage(item, dateConstraint)}
          key={optionValue}
        >
          {optionLabel}
        </ToggleGroup.Toggle>
      );
    }),
  ];

  if (!(hideAllDatesOption ?? false)) {
    toggleOptionList.push(
      <ToggleGroup.Toggle value={DATE_RANGE_TYPE.ALL} key={DATE_RANGE_TYPE.ALL}>
        All
      </ToggleGroup.Toggle>,
    );
  }

  return (
    <LazyTippy
      arrow={false}
      content={renderDropdown({ close: tippy.close })}
      offset={[0, 3]}
      interactive
      onCreate={tippy.set}
      onHide={() => setIsDropdownVisible(false)}
      onShow={() => setIsDropdownVisible(true)}
      placement="bottom-start"
      theme="custom"
      trigger="manual"
      zIndex={Z_INDEX}
      plugins={[hideOnEsc]}
      popperOptions={{
        modifiers: [{ name: 'flip', enabled: false }],
      }}
      appendTo={() => document.body} // To avoid overlapping issue (cf FN-1280)
      maxWidth="none"
    >
      <StyledToggleGroup
        description={filterName}
        onChange={(value) => {
          if (value === DATE_RANGE_TYPE.ABSOLUTE) {
            if (!isDropdownVisible) {
              tippy.open();
            }
          }
          if (value === DATE_RANGE_TYPE.ALL) {
            onApply(makeAllDateRange());
          } else {
            const dateRange = RELATIVE_DATE_RANGES.find((item) => stringify(item) === value);
            if (dateRange) {
              onApply(dateRange);
            }
          }
        }}
        selectedValue={currentStringValue}
        isAutoSelected={isAutoSelected === true}
      >
        {toggleOptionList}
      </StyledToggleGroup>
    </LazyTippy>
  );
}
